<template>
  <div class="profile container">
    <!-- Not logged in (edge case: route guard should normally prevent this) -->
    <div v-if="!user" class="profile__guard reveal">
      <div class="profile__peel">🍌🔒</div>
      <h1>Tu n'es pas connecté</h1>
      <p>Connecte-toi pour retrouver ton profil et tes blagues favorites.</p>
      <BananaButton to="/login" size="lg">Se connecter</BananaButton>
    </div>

    <template v-else>
      <!-- Header -->
      <header class="profile__head reveal">
        <div class="profile__avatar" aria-hidden="true">{{ initial }}</div>
        <div class="profile__id">
          <h1 class="profile__name">{{ user.displayName || pseudoFromEmail }}</h1>
          <p class="profile__email">{{ user.email }}</p>
          <p v-if="joinedLabel" class="profile__joined">Membre depuis {{ joinedLabel }}</p>
        </div>
        <BananaButton variant="danger" size="sm" class="profile__logout" @click="doLogout">
          Déconnexion
        </BananaButton>
      </header>

      <!-- Navigation hub -->
      <nav class="profile__hub reveal">
        <router-link to="/profil/favoris" class="hub-card">
          <span class="hub-card__emoji" aria-hidden="true">⭐</span>
          <span class="hub-card__body">
            <span class="hub-card__title">Mes favoris</span>
            <span class="hub-card__desc">Retrouve les blagues que tu as mises de côté.</span>
          </span>
          <span class="hub-card__arrow" aria-hidden="true">→</span>
        </router-link>

        <router-link to="/profil/mes-blagues" class="hub-card">
          <span class="hub-card__emoji" aria-hidden="true">✍️</span>
          <span class="hub-card__body">
            <span class="hub-card__title">Mes blagues</span>
            <span class="hub-card__desc">Consulte et retouche les blagues que tu as proposées.</span>
          </span>
          <span class="hub-card__arrow" aria-hidden="true">→</span>
        </router-link>
      </nav>

      <!-- Danger zone -->
      <section class="danger reveal">
        <div class="danger__head">
          <h2 class="danger__title">Zone de danger</h2>
          <p class="danger__desc">
            La suppression de ton compte est définitive. Tes votes et favoris
            seront effacés. Tes blagues et commentaires publics restent en ligne
            mais sont anonymisés (ton nom en est détaché).
          </p>
        </div>

        <template v-if="!confirmingDelete">
          <BananaButton variant="danger" size="sm" @click="confirmingDelete = true">
            Supprimer mon compte
          </BananaButton>
        </template>

        <div v-else class="danger__confirm">
          <p class="danger__confirm-q">
            Es-tu sûr·e ? Cette action est <strong>irréversible</strong>.
          </p>
          <p v-if="deleteError" class="danger__error">{{ deleteError }}</p>
          <div class="danger__actions">
            <BananaButton
              variant="danger"
              size="sm"
              :loading="deleting"
              @click="doDeleteAccount"
            >
              Oui, supprimer définitivement
            </BananaButton>
            <BananaButton
              variant="outline"
              size="sm"
              :disabled="deleting"
              @click="cancelDelete"
            >
              Annuler
            </BananaButton>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import BananaButton from '@/components/ui/BananaButton.vue'
import { DELETE_ACCOUNT } from '@/graphql/auth'
import { authState, logout } from '@/auth'

// Local query to grab the registration date, which isn't stored in
// authState.user (that only holds id/email/displayName/isStaff).
const ME_JOINED = gql`
  query MeJoined {
    me {
      id
      dateJoined
    }
  }
`

export default {
  name: 'ProfileView',
  components: { BananaButton },
  data() {
    return {
      authState,
      dateJoined: null,
      confirmingDelete: false,
      deleting: false,
      deleteError: '',
    }
  },
  computed: {
    user() {
      return this.authState.user
    },
    initial() {
      const src = this.user?.displayName || this.user?.email || '?'
      return src.trim().charAt(0).toUpperCase() || '?'
    },
    pseudoFromEmail() {
      return (this.user?.email || '').split('@')[0] || 'Banané·e'
    },
    joinedLabel() {
      if (!this.dateJoined) return ''
      const d = new Date(this.dateJoined)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })
    },
  },
  apollo: {
    me: {
      query: ME_JOINED,
      skip() { return !this.user },
      update(data) {
        if (data && data.me) this.dateJoined = data.me.dateJoined
        return data && data.me
      },
    },
  },
  methods: {
    doLogout() {
      logout()
      this.$router.push('/')
    },
    cancelDelete() {
      this.confirmingDelete = false
      this.deleteError = ''
    },
    async doDeleteAccount() {
      this.deleting = true
      this.deleteError = ''
      try {
        const { data } = await this.$apollo.mutate({
          mutation: DELETE_ACCOUNT,
          variables: { confirm: true },
        })
        if (!data || !data.deleteAccount || !data.deleteAccount.ok) {
          throw new Error('La suppression a échoué.')
        }
        logout()
        this.$router.push({ path: '/', query: { deleted: '1' } })
      } catch (e) {
        console.error('Error deleting account:', e)
        this.deleteError =
          "Impossible de supprimer le compte pour l'instant. Réessaie plus tard."
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<style scoped>
.profile { padding: 40px 20px 70px; max-width: 760px; }

/* Header */
.profile__head {
  display: flex;
  align-items: center;
  gap: 20px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-pop);
  padding: 24px 26px;
  margin-bottom: 36px;
  flex-wrap: wrap;
}
.profile__avatar {
  flex: 0 0 auto;
  width: 68px; height: 68px;
  display: grid; place-items: center;
  font-family: var(--font-display);
  font-weight: 700; font-size: 2rem;
  color: var(--ink-900);
  background: var(--banana-500);
  border: var(--border-w) solid var(--border-c);
  border-radius: 50%;
}
.profile__id { flex: 1 1 200px; min-width: 0; }
.profile__name { font-size: 1.5rem; margin: 0 0 2px; word-break: break-word; }
.profile__email { color: var(--text-soft); margin: 0; font-size: 0.95rem; word-break: break-word; }
.profile__joined {
  margin: 6px 0 0; font-size: 0.82rem; color: var(--text-soft);
  font-family: var(--font-accent); font-style: italic;
}
.profile__logout { flex: 0 0 auto; }

/* Navigation hub */
.profile__hub { display: flex; flex-direction: column; gap: 16px; }
.hub-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 22px 24px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-pop);
  text-decoration: none;
  color: var(--ink-900);
  transition: transform 0.15s var(--ease-out), box-shadow 0.15s var(--ease-out);
}
.hub-card:hover { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 var(--border-c); }
.hub-card:active { transform: translate(2px, 2px); box-shadow: 1px 1px 0 var(--border-c); }
.hub-card__emoji {
  flex: 0 0 auto;
  width: 58px; height: 58px;
  display: grid; place-items: center;
  font-size: 1.9rem;
  background: var(--banana-300);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
}
.hub-card__body { flex: 1 1 auto; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.hub-card__title { font-family: var(--font-display); font-weight: 800; font-size: 1.25rem; }
.hub-card__desc { color: var(--text-soft); font-size: 0.92rem; line-height: 1.4; }
.hub-card__arrow {
  flex: 0 0 auto;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.4rem;
  color: var(--banana-700);
}

/* Danger zone */
.danger {
  margin-top: 48px;
  padding: 24px 26px;
  border: var(--border-w) solid var(--bruise);
  border-radius: var(--r-xl);
  background: color-mix(in srgb, var(--bruise) 6%, var(--surface));
}
.danger__title {
  font-size: 1.2rem;
  margin: 0 0 8px;
  color: var(--bruise);
}
.danger__desc {
  margin: 0 0 18px;
  font-size: 0.9rem;
  line-height: 1.5;
  color: var(--text-soft);
  max-width: 560px;
}
.danger__confirm {
  border-top: 1px dashed color-mix(in srgb, var(--bruise) 40%, transparent);
  padding-top: 16px;
}
.danger__confirm-q { margin: 0 0 14px; font-size: 0.95rem; color: var(--ink-900); }
.danger__error {
  margin: 0 0 12px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--bruise);
}
.danger__actions { display: flex; gap: 12px; flex-wrap: wrap; }

/* Guard */
.profile__peel { font-size: 2.6rem; margin-bottom: 10px; }
.profile__guard { text-align: center; padding: 60px 20px; }
.profile__guard h1 { font-size: clamp(1.7rem, 5vw, 2.4rem); }
.profile__guard p { color: var(--text-soft); max-width: 420px; margin: 0 auto 24px; }

@media (max-width: 560px) {
  .profile { padding: 30px 20px 50px; }
  .profile__head { padding: 20px; }
  .profile__logout { width: 100%; }
}
</style>
